import React, {useEffect, useState} from "react";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Typography,
} from "@mui/material";
import {Cancel, CheckCircle} from "@mui/icons-material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {Link, useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";

import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import {executeMyCodeTask, getMyCodeTaskById, resetMyCodeTaskAttempts} from "../api/api";
import {CodeExecutionResponse} from "../types/coderunner";
import {CodeAttempt, CodeTask, CodeTaskAttemptResponse} from "../types";
import {useAlert} from "../context/AlertContext";
import BreadcrumbLink from "../components/BreadcrumbLink";
import MultilineText from "../helper/MultilineText";
import {useAuth} from "../context/AuthContext";
import CodeTextarea from "../components/CodeTextarea";

const mapBackendLanguageToEditor = (lang?: string): string => {
    switch (lang) {
        case "JAVA":
            return "java";
        case "PYTHON":
            return "python";
        case "GO":
            return "go";
        case "JAVA_CLASS":
            return "java";
        case "JAVA_METHOD":
            return "java";
        case "GO_STRUCT":
            return "go";
        default:
            return "java";
    }
};

const normalize = (v: any) => String(v ?? "").replace(/\r\n/g, "\n");
const hasDiff = (expected: any, actual: any) => normalize(expected) !== normalize(actual);

const HighlightedMultilineText = ({
                                      expected,
                                      actual,
                                      emptyText,
                                      maxHeight = 160,
                                  }: {
    expected: any;
    actual: any;
    emptyText: string;
    maxHeight?: number;
}) => {
    const e = normalize(expected);
    const a = normalize(actual);

    const text = a.length === 0 ? emptyText : a;

    if (a.length === 0) {
        return <MultilineText value={text} maxHeight={maxHeight}/>;
    }

    const maxLen = Math.max(e.length, a.length);

    return (
        <Box
            component="pre"
            sx={(theme) => ({
                m: 0,
                mt: 0.5,
                p: 1.25,
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor:
                    theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.03)",
                color: theme.palette.text.primary,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 13,
                lineHeight: 1.45,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight,
                overflow: "auto",
            })}
        >
            {Array.from({length: maxLen}).map((_, i) => {
                const expCh = e[i] ?? "";
                const actCh = a[i] ?? "";
                if (actCh === "") return null;

                const differs = actCh !== expCh;
                const displayCh = differs && actCh === " " ? "\u00A0" : actCh;

                return (
                    <span
                        key={i}
                        style={{
                            backgroundColor: differs ? "rgba(255, 193, 7, 0.45)" : "transparent",
                            borderRadius: differs ? 2 : 0,
                            padding: differs ? "0 1px" : 0,
                        }}
                    >
                        {displayCh}
                    </span>
                );
            })}
        </Box>
    );
};

const MyCodeTaskDetailsPage: React.FC = () => {
    const {t} = useTranslation();

    const {taskId} = useParams<{ taskId?: string }>();
    const safeTaskId = taskId ?? "";
    const navigate = useNavigate();

    const {showAlert} = useAlert();
    const {hasRole} = useAuth();
    const isTeacher = hasRole?.("ROLE_TEACHER") ?? false;

    const [myCodeAttempt, setMyCodeAttempt] = useState<CodeTaskAttemptResponse | null>(null);

    const [task, setTask] = useState<CodeTask | null>(null);
    const [attempt, setAttempt] = useState<CodeAttempt | null>(null);

    const [resetOpen, setResetOpen] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const hasAttempts = Boolean(attempt) && ((attempt?.attemptAmount ?? 0) > 0);

    const [language, setLanguage] = useState("java");
    const [code, setCode] = useState<string>("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [response, setResponse] = useState<CodeExecutionResponse | null>(null);

    const [pageLoading, setPageLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const [showDiff, setShowDiff] = useState<Record<number, boolean>>({});
    const toggleShowDiff = (idx: number) =>
        setShowDiff((prev) => ({...prev, [idx]: !prev[idx]}));

    useEffect(() => {
        setShowDiff({});
    }, [response]);

    useEffect(() => {
        if (!hasAttempts) {
            setResetOpen(false);
        }
    }, [hasAttempts]);

    useEffect(() => {
        if (!safeTaskId) return;

        let cancelled = false;

        const load = async () => {
            setPageLoading(true);
            setHasError(false);

            try {
                const id = Number(safeTaskId);
                const data: CodeTaskAttemptResponse = await getMyCodeTaskById(id);

                if (cancelled) return;

                setMyCodeAttempt(data);
                setTask(data.task);
                setAttempt(data.attempt ?? null);

                if (data.task?.language) {
                    setLanguage(mapBackendLanguageToEditor(data.task.language));
                }

                if (data.attempt?.codeSolution) {
                    setCode(data.attempt.codeSolution);
                } else if (data.task?.starterCode && data.task.starterCode.trim().length > 0) {
                    setCode(data.task.starterCode);
                } else {
                    setCode("");
                }

                if (data.attempt?.attemptResultContent) {
                    try {
                        const parsedResults = JSON.parse(
                            data.attempt.attemptResultContent
                        ) as CodeExecutionResponse["results"];

                        const restoredResponse: CodeExecutionResponse = {
                            compiledSuccessfully: true,
                            compileStdout: null,
                            compileStderr: null,
                            allPassed: Boolean(data.attempt.attemptResult),
                            results: parsedResults,
                        };

                        setResponse(restoredResponse);
                    } catch (parseError) {
                        console.error("Failed to parse attemptResultContent", parseError);
                        setResponse(null);
                    }
                } else {
                    setResponse(null);
                }

                setError(null);
            } catch (e: any) {
                if (cancelled) return;

                setHasError(true);
                setMyCodeAttempt(null);
                setTask(null);
                setAttempt(null);
                setResponse(null);

                showAlert(e?.message ?? t("errors.contactAdmins"), "error");
            } finally {
                if (!cancelled) setPageLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeTaskId]);

    const handleRunCode = async () => {
        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            const id = Number(safeTaskId);
            const res = await executeMyCodeTask(id, code);

            setResponse(res);
            setAttempt({
                id: attempt?.id ?? 0,
                codeSolution: code,
                attemptTime: new Date().toISOString(),
                attemptAmount: (attempt?.attemptAmount ?? 0) + 1,
                attemptResult: res.allPassed,
                attemptResultContent: JSON.stringify(res.results),
            });
        } catch (e: any) {
            console.error(e);
            setError(e?.message ?? t("mycodetaskdetails.errors.executeFailed"));
        } finally {
            setLoading(false);
        }
    };

    const handleResetAttempts = async () => {
        if (!hasAttempts) {
            showAlert(t("mycodetaskdetails.alerts.noAttemptsToReset"), "info");
            setResetOpen(false);
            return;
        }

        if (!safeTaskId) return;

        setResetLoading(true);
        try {
            await resetMyCodeTaskAttempts(Number(safeTaskId));

            setAttempt(null);
            setResponse(null);
            setError(null);
            setShowDiff({});

            if (task?.starterCode && task.starterCode.trim().length > 0) {
                setCode(task.starterCode);
            } else {
                setCode("");
            }

            showAlert(t("mycodetaskdetails.alerts.attemptsResetSuccess"), "success");
            setResetOpen(false);
        } catch (e: any) {
            console.error(e);
            showAlert(e?.message ?? t("mycodetaskdetails.errors.resetFailed"), "error");
        } finally {
            setResetLoading(false);
        }
    };

    const handleGoToPreviousTask = () => {
        if (!myCodeAttempt?.previousTaskId) return;
        navigate(`/task-details/${myCodeAttempt.previousTaskId}`);
    };

    const handleGoToNextTask = () => {
        if (!myCodeAttempt?.nextTaskId) return;
        navigate(`/task-details/${myCodeAttempt.nextTaskId}`);
    };

    if (pageLoading && !task) {
        return (
            <Container sx={{mt: 6, display: "flex", justifyContent: "center"}}>
                <CircularProgress/>
            </Container>
        );
    }

    if (hasError) {
        return (
            <Container sx={{mt: 4}}>
                <Button component={Link} to="/" variant="outlined">
                    {t("common.toMainPage")}
                </Button>
            </Container>
        );
    }

    if (!task) return null;

    return (
        <Box sx={{px: {xs: 3, md: 3}}}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                maxItems={3}
                itemsBeforeCollapse={1}
                itemsAfterCollapse={3}
                sx={{
                    mb: 2,
                    "& .MuiBreadcrumbs-ol": {
                        overflow: "hidden",
                    },
                    "& .MuiBreadcrumbs-li": {
                        minWidth: 0,
                    },
                }}
            >
                <BreadcrumbLink to="/my-courses">
                    {t("breadcrumb.myCourses")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/course-details/${myCodeAttempt?.courseId}`}>
                    {myCodeAttempt?.courseTitle || t("breadcrumb.courseDetails")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/chapter-details/${myCodeAttempt?.chapterId}`}>
                    {myCodeAttempt?.chapterTitle || t("breadcrumb.chapterDetails")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/lesson-details/${myCodeAttempt?.lessonId}`}>
                    {myCodeAttempt?.lessonTitle || t("breadcrumb.lessonDetails")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/lesson-details/${myCodeAttempt?.lessonId}/tasks`}>
                    {t("tabs.codeTasks")}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2">
                    <strong>{t("mycodetaskdetails.details")}</strong>
                </Typography>
            </Breadcrumbs>

            <Box
                sx={(theme) => ({
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    pb: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    gap: 2,
                    flexWrap: "wrap",
                })}
            >
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <Typography sx={{fontSize: 28, fontWeight: 800}} noWrap>
                        {task.title}
                    </Typography>

                    {!pageLoading && attempt && attempt.attemptResult !== null && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {attempt.attemptResult ? (
                                <>
                                    <CheckCircle fontSize="small" color="success"/>
                                    <Typography variant="body2" color="success.main">
                                        {t("mycodetaskdetails.solved")}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Cancel fontSize="small" color="error"/>
                                    <Typography variant="body2" color="error.main">
                                        {t("mycodetaskdetails.notSolved")}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    )}
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                        onClick={handleGoToPreviousTask}
                        disabled={!myCodeAttempt?.previousTaskId || pageLoading}
                        size="small"
                    >
                        <ArrowBackIosNewIcon fontSize="small"/>
                    </IconButton>

                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            minWidth: 72,
                            textAlign: "center",
                        }}
                    >
                        {myCodeAttempt?.currentTaskIndex ?? 1} / {myCodeAttempt?.totalTasks ?? 1}
                    </Typography>

                    <IconButton
                        onClick={handleGoToNextTask}
                        disabled={!myCodeAttempt?.nextTaskId || pageLoading}
                        size="small"
                    >
                        <ArrowForwardIosIcon fontSize="small"/>
                    </IconButton>
                </Box>
            </Box>

            <Box
                sx={(theme) => ({
                    mb: 3,
                    pb: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                })}
            >
                <Typography variant="body1" dangerouslySetInnerHTML={{__html: task.content || ""}}/>
            </Box>

            <Paper
                elevation={0}
                sx={(theme) => ({
                    p: 0,
                    border: `1px solid ${theme.palette.divider}`,
                    mb: 2,
                })}
            >
                <CodeTextarea
                    height={400}
                    language={language}
                    value={code}
                    onChange={setCode}
                />

                <Box
                    sx={(theme) => ({
                        px: 2,
                        py: 1.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                    })}
                >
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        {isTeacher && (
                            <Button
                                component={Link}
                                to={`/teacher/code-tasks/${safeTaskId}/attempts`}
                                variant="outlined"
                            >
                                {t("mycodetaskdetails.viewAttempts")}
                            </Button>
                        )}

                        {hasAttempts && (
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setResetOpen(true)}
                                disabled={!hasAttempts || resetLoading || pageLoading}
                                sx={{textTransform: "none"}}
                            >
                                {t("mycodetaskdetails.resetAttempts")}
                            </Button>
                        )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        {loading && <CircularProgress size={24}/>}
                        <Button variant="contained" onClick={handleRunCode} disabled={loading || !code.trim()}>
                            {t("mycodetaskdetails.runCode")}
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Box sx={{px: 2, pb: 2}}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                )}
            </Paper>

            {response && (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        p: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 2,
                    })}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {t("mycodetaskdetails.executionResult")}
                        </Typography>

                        <Box display="flex" gap={1} flexWrap="wrap">
                            <Chip
                                label={
                                    response.compiledSuccessfully
                                        ? t("mycodetaskdetails.compiled")
                                        : t("mycodetaskdetails.compilationError")
                                }
                                color={response.compiledSuccessfully ? "success" : "error"}
                                size="small"
                            />

                            <Chip
                                label={
                                    response.allPassed
                                        ? t("mycodetaskdetails.allTestsPassed")
                                        : t("mycodetaskdetails.someTestsFailed")
                                }
                                color={response.allPassed ? "success" : "warning"}
                                size="small"
                            />
                        </Box>
                    </Box>

                    <Divider sx={{my: 2}}/>

                    <Typography variant="subtitle2" gutterBottom>
                        {t("mycodetaskdetails.testCases")}
                    </Typography>

                    {response.results.length === 0 && (
                        <Typography variant="body2">
                            {t("mycodetaskdetails.noTestResults")}
                        </Typography>
                    )}

                    {response.results.length > 0 && (
                        <List dense sx={{mt: 1}}>
                            {response.results.map((r, index) => {
                                const expected = r.expectedOutput ?? "";
                                const actual = r.actualOutput ?? "";
                                const diff = hasDiff(expected, actual);

                                return (
                                    <ListItem
                                        key={index}
                                        alignItems="flex-start"
                                        sx={(theme) => ({
                                            borderBottom:
                                                index === response.results.length - 1
                                                    ? "none"
                                                    : `1px solid ${theme.palette.divider}`,
                                            py: 1.25,
                                        })}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    gap={1}
                                                >
                                                    <Typography variant="body1">
                                                        {t("mycodetaskdetails.test", {number: index + 1})}
                                                    </Typography>

                                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                        {diff && (
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                onClick={() => toggleShowDiff(index)}
                                                                sx={{textTransform: "none"}}
                                                            >
                                                                {showDiff[index]
                                                                    ? t("mycodetaskdetails.hideDifference")
                                                                    : t("mycodetaskdetails.showDifference")}
                                                            </Button>
                                                        )}

                                                        <Chip
                                                            label={
                                                                r.passed
                                                                    ? t("mycodetaskdetails.passed")
                                                                    : t("mycodetaskdetails.failed")
                                                            }
                                                            color={r.passed ? "success" : "error"}
                                                            size="small"
                                                        />
                                                    </Box>
                                                </Box>
                                            }
                                            secondary={
                                                <Box mt={1}>
                                                    <Box mt={1}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{fontWeight: 700, textTransform: "uppercase"}}
                                                        >
                                                            {t("mycodetaskdetails.input")}
                                                        </Typography>
                                                        <MultilineText value={r.input} maxHeight={90}/>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            display: "grid",
                                                            gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
                                                            gap: 1.5,
                                                            mt: 1,
                                                        }}
                                                    >
                                                        <Box>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{fontWeight: 700, textTransform: "uppercase"}}
                                                                >
                                                                    {t("mycodetaskdetails.expected")}
                                                                </Typography>
                                                            </Box>
                                                            <MultilineText value={expected}/>
                                                        </Box>

                                                        <Box>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{fontWeight: 700, textTransform: "uppercase"}}
                                                                >
                                                                    {t("mycodetaskdetails.actual")}
                                                                </Typography>
                                                            </Box>

                                                            {diff && showDiff[index] ? (
                                                                <HighlightedMultilineText
                                                                    expected={expected}
                                                                    actual={actual}
                                                                    emptyText={t("mycodetaskdetails.emptyOutput")}
                                                                />
                                                            ) : (
                                                                <MultilineText value={actual}/>
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    <Typography variant="body2" sx={{mt: 0.5}}>
                                                        <strong>{t("mycodetaskdetails.message")}</strong> {r.message}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Paper>
            )}

            <DeleteConfirmationModal
                open={resetOpen}
                onClose={() => setResetOpen(false)}
                onConfirm={handleResetAttempts}
            />
        </Box>
    );
};

export default MyCodeTaskDetailsPage;